import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { productService } from '../../services/productService';
import { aiKitGenerationService } from '../../services/aiKitGenerationService';
import { triggerAutoReload } from '../../utils/autoReload';

const KitSuggestionsTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kits, setKits] = useState([]);
  const [creatingKit, setCreatingKit] = useState(null);
  const [rotationSeed, setRotationSeed] = useState(0);
  const [error, setError] = useState(null);
  const [kitQuantity, setKitQuantity] = useState(10);
  const [kitStrategy, setKitStrategy] = useState('balanced');
  const [activeStrategyTab, setActiveStrategyTab] = useState('all');

  const strategyOptions = [
    { value: 'balanced', label: 'Balanceado - Mix de estratégias' },
    { value: 'complementary', label: 'Complementar - Produtos que funcionam juntos' },
    { value: 'same_category', label: 'Mesma Categoria - Produtos similares' },
    { value: 'high_margin', label: 'Alta Margem - Foco em lucratividade' },
    { value: 'quick_turnover', label: 'Giro Rápido - Produtos populares' }
  ];

  const strategyTabs = [
    { id: 'all', label: 'Todos', icon: 'Grid' },
    { id: 'balanced', label: 'Balanceado', icon: 'Scale' },
    { id: 'complementary', label: 'Complementar', icon: 'Link' },
    { id: 'same_category', label: 'Mesma Categoria', icon: 'Layers' },
    { id: 'high_margin', label: 'Alta Margem', icon: 'TrendingUp' },
    { id: 'quick_turnover', label: 'Giro Rápido', icon: 'Zap' }
  ];

  // Load kits from localStorage on mount
  useEffect(() => {
    const savedKits = localStorage.getItem('persistentKitSuggestions');
    if (savedKits) {
      try {
        const parsed = JSON.parse(savedKits);
        setKits(parsed);
      } catch (error) {
        console.error('Error loading saved kits:', error);
      }
    } else {
      loadKitSuggestions();
    }
  }, []);

  // Save kits to localStorage whenever they change
  useEffect(() => {
    if (kits?.length > 0) {
      localStorage.setItem('persistentKitSuggestions', JSON.stringify(kits));
    }
  }, [kits]);

  useEffect(() => {
    if (rotationSeed > 0) {
      loadKitSuggestions();
    }
  }, [rotationSeed]);

  const loadKitSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // CRITICAL FIX: Ensure exact quantity is generated
      const requestedQuantity = Math.min(Math.max(parseInt(kitQuantity) || 10, 1), 50);
      
      const result = await aiKitGenerationService?.generateRotatingKits({
        maxKits: requestedQuantity, // Use exact requested quantity
        minProducts: 2,
        maxProducts: 5,
        seed: rotationSeed,
        strategy: kitStrategy
      });

      if (result?.success) {
        // CRITICAL FIX: Ensure we have exactly the requested quantity
        let generatedKits = result?.kits || [];
        
        // If we got fewer kits than requested, generate more with different seeds
        if (generatedKits?.length < requestedQuantity) {
          const additionalNeeded = requestedQuantity - generatedKits?.length;
          for (let i = 0; i < additionalNeeded; i++) {
            const additionalResult = await aiKitGenerationService?.generateRotatingKits({
              maxKits: 1,
              minProducts: 2,
              maxProducts: 5,
              seed: rotationSeed + i + 1000,
              strategy: kitStrategy
            });
            if (additionalResult?.success && additionalResult?.kits?.length > 0) {
              generatedKits = [...generatedKits, ...additionalResult?.kits];
            }
          }
        }
        
        // Ensure we don't exceed the requested quantity
        generatedKits = generatedKits?.slice(0, requestedQuantity);
        
        setKits(generatedKits);
        localStorage.setItem('persistentKitSuggestions', JSON.stringify(generatedKits));
        
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'success',
            message: `${generatedKits?.length} kits gerados com sucesso`,
            progress: 100
          }
        }));
      } else {
        setError(result?.error || 'Erro ao gerar sugestões de kits');
        setKits([]);
      }
    } catch (error) {
      console.error('Error loading kit suggestions:', error);
      setError(error?.message || 'Erro ao carregar sugestões');
      setKits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNewKits = () => {
    setRotationSeed(prev => prev + 1);
  };

  const handleClearKits = () => {
    setKits([]);
    localStorage.removeItem('persistentKitSuggestions');
    window.dispatchEvent(new CustomEvent('statusUpdate', {
      detail: {
        type: 'info',
        message: 'Sugestões de kits limpas',
        progress: 100
      }
    }));
  };

  const handleCreateKit = async (kit) => {
    try {
      setCreatingKit(kit?.name);
      
      // Collect all images from all products in the kit
      const allImages = [];
      kit?.products?.forEach(product => {
        if (product?.images && Array.isArray(product?.images)) {
          allImages?.push(...product?.images);
        }
      });
      
      // Remove duplicates
      const uniqueImages = [...new Set(allImages)];
      
      // Create kit as a draft product in the catalog
      const kitProduct = {
        name: kit?.name,
        sku: `KIT-${Date.now()}-${Math.random()?.toString(36)?.substring(2, 7)?.toUpperCase()}`,
        productType: 'kit',
        price: kit?.kitPrice,
        description: kit?.reason || `Kit promocional com ${kit?.products?.length} produtos`,
        images: uniqueImages,
        status: 'draft',
        category: kit?.products?.[0]?.category || 'Kit',
        metadata: {
          kitProducts: kit?.products?.map(p => ({
            id: p?.id,
            name: p?.name,
            quantity: 1,
            price: p?.price
          })),
          targetAudience: kit?.targetAudience,
          discount: kit?.discount,
          estimatedProfit: kit?.savings,
          aiGenerated: true
        }
      };

      const createdKit = await productService?.create(kitProduct);

      // Trigger auto-reload
      triggerAutoReload('kit-suggestion-saved');

      // Show success feedback
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `Kit "${kit?.name}" salvo com sucesso!`,
          progress: 100
        }
      }));
      
      // Remove the kit from suggestions after creation
      const updatedKits = kits?.filter(k => k?.name !== kit?.name);
      setKits(updatedKits);
      localStorage.setItem('persistentKitSuggestions', JSON.stringify(updatedKits));
      
      // Redirect to Kits tab where the user can see and edit the created kit
      setTimeout(() => {
        navigate('/product-catalog-manager?tab=kits');
      }, 800);
    } catch (error) {
      console.error('Error creating kit:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao criar kit',
          details: error?.message
        }
      }));
    } finally {
      setCreatingKit(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Icon name="Loader" size={40} className="animate-spin text-blue-600 mb-4" />
        <p className="text-sm text-muted-foreground">Gerando {kitQuantity} sugestões inteligentes com IA...</p>
        <p className="text-xs text-muted-foreground mt-2">Isso pode levar alguns segundos</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <Icon name="AlertCircle" size={56} className="text-destructive mx-auto mb-4" />
        <p className="text-lg text-foreground mb-2">Erro ao gerar sugestões</p>
        <p className="text-sm text-muted-foreground mb-6">{error}</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={loadKitSuggestions} variant="outline">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Tentar Novamente
          </Button>
          <Button onClick={() => navigate('/ai-api-configuration-center')} variant="primary">
            <Icon name="Settings" size={16} className="mr-2" />
            Configurar IA
          </Button>
        </div>
      </div>
    );
  }

  // Filter kits based on active strategy tab
  const filteredKits = activeStrategyTab === 'all' 
    ? kits 
    : kits?.filter(kit => {
        // Match kit strategy based on its characteristics
        if (activeStrategyTab === 'high_margin') {
          return kit?.savings > 50; // High margin kits
        }
        if (activeStrategyTab === 'complementary') {
          return kit?.products?.length >= 3; // Complementary kits have more products
        }
        if (activeStrategyTab === 'same_category') {
          const categories = kit?.products?.map(p => p?.category);
          return new Set(categories)?.size === 1; // All same category
        }
        return true;
      });

  return (
    <div className="space-y-6">
      {/* Header with Options */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-2">Sugestões de Kits Inteligentes</h3>
          <p className="text-sm text-muted-foreground">Kits gerados por IA com análise de compatibilidade e coerência comercial</p>
          {kits?.length > 0 && (
            <p className="text-xs text-blue-600 mt-1">✓ {kits?.length} sugestões salvas (persistentes entre abas)</p>
          )}
        </div>
        <div className="flex gap-2">
          {kits?.length > 0 && (
            <Button onClick={handleClearKits} variant="outline" className="shadow-sm text-destructive">
              <Icon name="Trash2" size={18} className="mr-2" />
              Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Generation Options Panel - Always Visible */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6 mb-6">
        <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Sliders" size={18} className="text-primary" />
          Opções de Geração de Kits
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Quantidade de Kits</label>
            <Input
              type="number"
              min="1"
              max="50"
              value={kitQuantity}
              onChange={(e) => setKitQuantity(parseInt(e?.target?.value) || 10)}
              placeholder="Número de kits"
            />
            <p className="text-xs text-muted-foreground mt-1">Quantos kits gerar (1-50)</p>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Estratégia de Geração</label>
            <Select
              value={kitStrategy}
              onChange={(e) => setKitStrategy(e?.target?.value)}
              options={strategyOptions}
            />
            <p className="text-xs text-muted-foreground mt-1">Tipo de kits a gerar</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleGenerateNewKits} variant="primary" className="shadow-md">
            <Icon name="Sparkles" size={16} className="mr-2" />
            Gerar {kitQuantity} Kits
          </Button>
        </div>
      </div>

      {/* Strategy Navigation Tabs */}
      {kits?.length > 0 && (
        <div className="border-b border-border mb-6">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {strategyTabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveStrategyTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeStrategyTab === tab?.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {filteredKits?.length === 0 ? (
        <div className="text-center py-16">
          <Icon name="Package" size={56} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground mb-2">
            {kits?.length === 0 ? 'Nenhum kit sugerido no momento' : 'Nenhum kit nesta categoria'}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {kits?.length === 0 ? 'Clique em "Gerar Kits" para criar sugestões' : 'Tente outra categoria'}
          </p>
          {kits?.length === 0 && (
            <Button onClick={loadKitSuggestions} variant="outline">
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Gerar Kits
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredKits?.map((kit, index) => (
            <div key={index} className="bg-background border border-border rounded-xl p-6 hover:shadow-lg transition-smooth">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">{kit?.name}</h3>
                  <span className="inline-block px-3 py-1 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-600">
                    🤖 Gerado por IA
                  </span>
                </div>
              </div>

              {/* Target Audience */}
              {kit?.targetAudience && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Público-alvo:</p>
                  <p className="text-sm text-foreground font-medium">{kit?.targetAudience}</p>
                </div>
              )}

              {/* Product thumbnails */}
              <div className="flex flex-wrap gap-3 mb-4">
                {kit?.products?.slice(0, 5)?.map((product, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
                    <div className="w-10 h-10 bg-muted rounded overflow-hidden">
                      {product?.images?.[0] ? (
                        <img src={product?.images?.[0]} alt={product?.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="Package" size={14} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-foreground truncate max-w-[120px]">{product?.name}</span>
                  </div>
                ))}
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-1">Preço Individual</p>
                  <p className="font-semibold text-foreground">R$ {kit?.totalIndividualPrice?.toFixed(2)}</p>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-1">Preço do Kit</p>
                  <p className="font-semibold text-blue-600">R$ {kit?.kitPrice?.toFixed(2)}</p>
                </div>
                <div className="bg-success/10 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs mb-1">Economia</p>
                  <p className="font-semibold text-success">R$ {kit?.savings?.toFixed(2)}</p>
                </div>
              </div>

              {/* AI Justification */}
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 mb-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">💡 Análise da IA:</p>
                <p className="text-sm text-foreground leading-relaxed">
                  {kit?.reason}
                </p>
              </div>

              <Button
                onClick={() => handleCreateKit(kit)}
                variant="primary"
                className="w-full bg-slate-700 hover:bg-slate-800 text-white"
                disabled={creatingKit === kit?.name}
              >
                {creatingKit === kit?.name ? (
                  <>
                    <Icon name="Loader" size={18} className="mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Icon name="Plus" size={18} className="mr-2" />
                    Criar Kit
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitSuggestionsTab;